import React, { useEffect, useState } from 'react';
import { getOrderDetails } from '../api/order';
import UserLayout from './UserLayout';
import { useParams, useNavigate } from 'react-router-dom';
import { resolveImageUrl } from '../utils/image';
import StatutoryComplianceNotice from './StatutoryComplianceNotice';
import './OrderDetailsPage.css';

export default function OrderDetailsPage({ user, setUser }: { user: any, setUser: (user: any) => void }) {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchOrder() {
      try {
        const data = await getOrderDetails(Number(orderId));
        setOrder(data);
      } catch (err) {
        setOrder(null);
      } finally {
        setLoading(false);
      }
    }
    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <UserLayout user={user} setUser={setUser} hideSlider={true}>
        <div className="order-details-page-wrapper">
          <div className="order-details-container">
            <div className="od-loading">
              <div className="od-spinner"></div>
              <p>Loading order #{orderId} details...</p>
            </div>
          </div>
        </div>
      </UserLayout>
    );
  }

  if (!order) {
    return (
      <UserLayout user={user} setUser={setUser} hideSlider={true}>
        <div className="order-details-page-wrapper">
          <div className="order-details-container">
            <div className="od-not-found">
              <h2>Order #{orderId} Not Found</h2>
              <p>We couldn't retrieve the details for this order. It may have been archived or removed.</p>
              <button className="od-home-btn" onClick={() => navigate('/my-orders')}>
                Back to My Orders
              </button>
            </div>
          </div>
        </div>
      </UserLayout>
    );
  }

  const totalOrderAmount = order.products?.reduce((sum: number, p: any) => sum + (p.productRate * p.quantity), 0) || order.totalPrice || 0;
  const formattedDate = order.orderDate
    ? new Date(order.orderDate).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });

  return (
    <UserLayout user={user} setUser={setUser} hideSlider={true}>
      <div className="order-details-page-wrapper">
        <div className="order-details-container">
          {/* Header */}
          <div className="od-header">
            <button className="od-back-btn" onClick={() => navigate(-1)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
              <span>Back</span>
            </button>
            <div className="od-title-wrap">
              <h1 className="od-title">Order #{orderId}</h1>
              <span className="od-status-badge">
                {order.isComplete ? 'COMPLETED' : 'PROCESSING / PENDING ADVANCE'}
              </span>
            </div>
          </div>

          {/* Thank You Confirmation Banner (Image 2 style) */}
          <div className="od-thank-you-banner">
            <span className="od-check-icon">✓</span>
            <span>Thank you. Your order has been received.</span>
          </div>

          {/* Order Overview Strip */}
          <div className="od-summary-card">
            <div className="od-meta-item">
              <span className="od-meta-label">Order number:</span>
              <span className="od-meta-value">#{orderId}</span>
            </div>
            <div className="od-meta-item">
              <span className="od-meta-label">Date:</span>
              <span className="od-meta-value">{formattedDate}</span>
            </div>
            <div className="od-meta-item">
              <span className="od-meta-label">Total:</span>
              <span className="od-meta-value highlight">₹{Number(totalOrderAmount).toFixed(2)}</span>
            </div>
          </div>

          {/* Statutory Compliance Notice Banner (Image 2 style) */}
          <StatutoryComplianceNotice
            variant="order-received"
            storeName="Jai Bhavani Fireworks"
            phone="+91-7990696364"
            whatsappNumber="917990696364"
          />

          <div className="od-grid-layout">
            {/* Left: Products list */}
            <div className="od-products-card">
              <div className="od-card-header">
                <h3>Ordered Fireworks ({order.products?.length || 0} varieties)</h3>
              </div>

              <div className="od-products-list">
                {order.products && order.products.map((p: any) => {
                  const imgSrc = p.productImageUrl ? resolveImageUrl(p.productImageUrl) : null;
                  const itemTotal = Number(p.productRate || 0) * Number(p.quantity || 1);

                  return (
                    <div key={p.productId} className="od-product-item">
                      <div className="od-product-thumb">
                        {imgSrc ? (
                          <img src={imgSrc} alt={p.productName} loading="lazy" />
                        ) : (
                          <div className="od-thumb-placeholder">🧨</div>
                        )}
                      </div>

                      <div className="od-product-info">
                        <span className="od-product-name">{p.productName}</span>
                        {p.productCategoryName && (
                          <span className="od-product-category">Category: {p.productCategoryName}</span>
                        )}
                        <div className="od-product-rates">
                          <span>₹{Number(p.productRate).toFixed(2)} × {p.quantity} units</span>
                        </div>
                      </div>

                      <div className="od-product-total">
                        ₹{itemTotal.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="od-totals-strip">
                <div className="od-total-row">
                  <span>Grand Total:</span>
                  <span className="od-grand-total">₹{Number(totalOrderAmount).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Right: Customer & Address details card */}
            <div className="od-sidebar-column">
              <div className="od-shipping-card">
                <h3 className="od-sidebar-title">Customer &amp; Address Information</h3>
                
                <div className="od-info-list">
                  <div className="od-info-item">
                    <span className="od-info-label">Customer Address:</span>
                    <span className="od-info-value">{order.address || 'Customer Location'}</span>
                  </div>

                  <div className="od-info-item">
                    <span className="od-info-label">City / State:</span>
                    <span className="od-info-value">{order.city ? `${order.city}, ${order.state || 'Gujarat'}` : (order.state || 'Gujarat, India')}</span>
                  </div>

                  <div className="od-info-item">
                    <span className="od-info-label">Postal PIN:</span>
                    <span className="od-info-value">{order.postalCode || 'N/A'}</span>
                  </div>

                  <div className="od-info-item">
                    <span className="od-info-label">Contact Phone:</span>
                    <span className="od-info-value">{order.phoneNumber || 'N/A'}</span>
                  </div>
                </div>

                <div className="od-wa-help-box">
                  <strong>Need help with this order?</strong>
                  <p>Our team is available to assist you with order status and payment updates.</p>
                  <a
                    href={`https://wa.me/917990696364?text=Hello%20Jai%20Bhavani%20Fireworks,%20I%20need%20status%20update%20for%20Order%20%23${orderId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="od-wa-btn"
                  >
                    💬 Chat on WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </UserLayout>
  );
}